import type {
    CurrencyConfig,
    MovementPeriod,
    MovementType,
    ProductSort,
    Settings,
    StatusFilter,
    StockStatus
} from '@/types'

export const DEFAULT_BUSINESS_NAME = 'Mi negocio'

export const DEFAULT_CURRENCY = 'EUR'

export const DEFAULT_SETTINGS: Settings = {
    id: '',
    businessName: DEFAULT_BUSINESS_NAME,
    currency: DEFAULT_CURRENCY
}

export const DEFAULT_UNIT = 'ud.'

export const MOVEMENTS_PAGE_SIZE = 25

export const PRODUCT_HISTORY_LIMIT = 50

export const ACTIVITY_DAYS = 14

export const LIMITS = {
    name: 120,
    sku: 40,
    unit: 20,
    location: 80,
    supplier: 120,
    reason: 60,
    note: 240,
    categoryName: 40,
    businessName: 80,
    maxQuantity: 1000000000,
    maxMoney: 1000000000
}

export const MOVEMENT_TYPES: MovementType[] = ['entrada', 'salida', 'ajuste']

type MovementTypeMeta = {
    label: string
    plural: string
    submitLabel: string
    quantityLabel: string
    quantityHint: string
    successLabel: string
}

export const MOVEMENT_TYPE_META: Record<MovementType, MovementTypeMeta> = {
    entrada: {
        label: 'Entrada',
        plural: 'Entradas',
        submitLabel: 'Registrar entrada',
        quantityLabel: 'Cantidad que entra',
        quantityHint: 'Se sumará al stock actual.',
        successLabel: 'Entrada registrada'
    },
    salida: {
        label: 'Salida',
        plural: 'Salidas',
        submitLabel: 'Registrar salida',
        quantityLabel: 'Cantidad que sale',
        quantityHint: 'Se restará del stock actual.',
        successLabel: 'Salida registrada'
    },
    ajuste: {
        label: 'Ajuste',
        plural: 'Ajustes',
        submitLabel: 'Guardar ajuste',
        quantityLabel: 'Stock contado',
        quantityHint: 'Escribe las unidades que hay realmente. El stock pasará a ser esta cifra.',
        successLabel: 'Ajuste guardado'
    }
}

export const MOVEMENT_REASONS: Record<MovementType, string[]> = {
    entrada: [
        'Compra a proveedor',
        'Devolución de cliente',
        'Producción propia',
        'Stock inicial',
        'Otra entrada'
    ],
    salida: [
        'Venta',
        'Uso interno',
        'Merma o rotura',
        'Devolución a proveedor',
        'Otra salida'
    ],
    ajuste: [
        'Recuento de inventario',
        'Corrección de error',
        'Otro ajuste'
    ]
}

export const STOCK_STATUS_META: Record<StockStatus, { label: string }> = {
    ok: { label: 'En stock' },
    low: { label: 'Stock bajo' },
    out: { label: 'Agotado' }
}

export const STATUS_FILTERS: { value: StatusFilter, label: string }[] = [
    { value: 'todos', label: 'Todos' },
    { value: 'reponer', label: 'Por reponer' },
    { value: 'agotados', label: 'Agotados' },
    { value: 'disponibles', label: 'En stock' }
]

export const PRODUCT_SORTS: { value: ProductSort, label: string }[] = [
    { value: 'nombre', label: 'Nombre (A-Z)' },
    { value: 'stock-asc', label: 'Menos stock primero' },
    { value: 'stock-desc', label: 'Más stock primero' },
    { value: 'valor', label: 'Mayor valor primero' },
    { value: 'recientes', label: 'Actualizados recientemente' }
]

export const MOVEMENT_PERIODS: { value: MovementPeriod, label: string, days: number | null }[] = [
    { value: '7d', label: 'Últimos 7 días', days: 7 },
    { value: '30d', label: 'Últimos 30 días', days: 30 },
    { value: '90d', label: 'Últimos 90 días', days: 90 },
    { value: 'all', label: 'Todo el historial', days: null }
]

export const UNIT_SUGGESTIONS = [
    'ud.',
    'caja',
    'paquete',
    'pack',
    'par',
    'rollo',
    'bolsa',
    'botella',
    'kg',
    'l',
    'm'
]

export const CATEGORY_COLORS = [
    '#2563eb',
    '#0d9488',
    '#d97706',
    '#9333ea',
    '#db2777',
    '#dc2626',
    '#16a34a',
    '#475569'
]

export const CURRENCIES: CurrencyConfig[] = [
    { code: 'EUR', label: 'Euro', symbol: '€', symbolPosition: 'after', spaced: true, decimals: 2, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'USD', label: 'Dólar estadounidense', symbol: 'US$', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'MXN', label: 'Peso mexicano', symbol: '$', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'ARS', label: 'Peso argentino', symbol: '$', symbolPosition: 'before', spaced: true, decimals: 2, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'COP', label: 'Peso colombiano', symbol: '$', symbolPosition: 'before', spaced: true, decimals: 0, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'CLP', label: 'Peso chileno', symbol: '$', symbolPosition: 'before', spaced: false, decimals: 0, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'PEN', label: 'Sol peruano', symbol: 'S/', symbolPosition: 'before', spaced: true, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'UYU', label: 'Peso uruguayo', symbol: '$', symbolPosition: 'before', spaced: true, decimals: 2, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'BOB', label: 'Boliviano', symbol: 'Bs', symbolPosition: 'before', spaced: true, decimals: 2, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'PYG', label: 'Guaraní', symbol: '₲', symbolPosition: 'before', spaced: true, decimals: 0, decimalSeparator: ',', groupSeparator: '.' },
    { code: 'GTQ', label: 'Quetzal', symbol: 'Q', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'CRC', label: 'Colón costarricense', symbol: '₡', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: ',', groupSeparator: ' ' },
    { code: 'DOP', label: 'Peso dominicano', symbol: 'RD$', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'HNL', label: 'Lempira', symbol: 'L', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'NIO', label: 'Córdoba', symbol: 'C$', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'PAB', label: 'Balboa', symbol: 'B/.', symbolPosition: 'before', spaced: false, decimals: 2, decimalSeparator: '.', groupSeparator: ',' },
    { code: 'VES', label: 'Bolívar', symbol: 'Bs.', symbolPosition: 'before', spaced: true, decimals: 2, decimalSeparator: ',', groupSeparator: '.' }
]
